/**
 * Estate Flow - Universal Referral Dashboard (Enhanced)
 * Copyright © 2025 Sebastian Alliance Group LLC. All Rights Reserved.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  DollarSign, 
  Users, 
  Link as LinkIcon, 
  Copy, 
  CheckCircle,
  TrendingUp,
  Gift,
  Share2,
  Loader2,
  AlertCircle,
  Trophy,
  BarChart3,
  Bell,
  BellOff,
  Crown,
  Medal,
  Building2,
  CreditCard,
  History,
  Settings,
  Wallet
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Demo data for marketing screenshots
const DEMO_DATA = {
  has_referral_code: true,
  referral_code: "SARAH-VIP25",
  referral_link: "https://apcestateflow.com/?ref=SARAH-VIP25",
  stats: {
    total_signups: 47,
    active_referrals: 38,
    pending_payout: 1247.50,
    lifetime_earnings: 4892.75
  },
  recent_signups: [
    { name: "Michael Chen", plan: "HOA Community", date: "Dec 15, 2024", status: "active" },
    { name: "Sarah Williams", plan: "Landlord", date: "Dec 12, 2024", status: "active" },
    { name: "James Rodriguez", plan: "Property Manager", date: "Dec 10, 2024", status: "active" },
    { name: "Emily Thompson", plan: "HOA Community", date: "Dec 8, 2024", status: "active" },
    { name: "David Kim", plan: "Landlord", date: "Dec 5, 2024", status: "active" }
  ],
  recent_commissions: [
    { name: "Michael Chen", amount: 150.00, date: "Dec 15, 2024" },
    { name: "Sarah Williams", amount: 49.75, date: "Dec 12, 2024" },
    { name: "James Rodriguez", amount: 95.00, date: "Dec 10, 2024" }
  ]
};

const DEMO_LEADERBOARD = [
  { rank: 1, name: "Top Earner Sarah", referrals: 47, earnings: 4892.75 },
  { rank: 2, name: "Alex Partner", referrals: 35, earnings: 3654.00 },
  { rank: 3, name: "Maria Realty", referrals: 28, earnings: 2847.50 },
  { rank: 4, name: "John Broker", referrals: 22, earnings: 2156.25 },
  { rank: 5, name: "Lisa Agent", referrals: 18, earnings: 1823.00 }
];

const DEMO_CHART = [
  { month: "Jul", earnings: 245 },
  { month: "Aug", earnings: 387 },
  { month: "Sep", earnings: 512 },
  { month: "Oct", earnings: 689 },
  { month: "Nov", earnings: 892 },
  { month: "Dec", earnings: 1247 }
];

const ReferralDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [earningsChart, setEarningsChart] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [payoutSettings, setPayoutSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingPayout, setSavingPayout] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  
  // Payout settings form
  const [payoutForm, setPayoutForm] = useState({
    payout_method: 'bank_transfer',
    bank_name: '',
    bank_branch: '',
    account_holder_name: '',
    account_number: '',
    routing_number: '',
    account_type: 'checking',
    paypal_email: '',
    zelle_email: '',
    // Visa Direct fields
    visa_card_number: '',
    visa_card_expiry: '',
    visa_card_holder_name: ''
  });
  
  // Check for demo mode via URL parameter
  const isDemo = new URLSearchParams(window.location.search).get('demo') === 'true';

  useEffect(() => {
    if (isDemo) {
      // Load demo data for screenshots
      setDashboardData(DEMO_DATA);
      setLeaderboard(DEMO_LEADERBOARD);
      setEarningsChart(DEMO_CHART);
    } else {
      fetchAllData();
    }
  }, [isDemo]);

  const fetchAllData = async () => {
    await Promise.all([
      fetchDashboardData(),
      fetchLeaderboard(),
      fetchEarningsChart(),
      fetchPayoutSettings(),
      fetchPayoutHistory()
    ]);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`${API}/referral/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching referral dashboard:', error);
      if (error.response?.status === 404 || error.response?.data?.has_referral_code === false) {
        setDashboardData({ has_referral_code: false });
      } else {
        toast.error('Failed to load referral data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/referral/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const fetchEarningsChart = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/referral/earnings-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEarningsChart(response.data.chart_data || []);
    } catch (error) {
      console.error('Error fetching earnings chart:', error);
    }
  };

  const fetchPayoutSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/referral/payout-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setPayoutSettings(response.data);
        setPayoutForm(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      console.error('Error fetching payout settings:', error);
    }
  };

  const fetchPayoutHistory = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/referral/payout-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayoutHistory(response.data.payouts || []);
    } catch (error) {
      console.error('Error fetching payout history:', error);
    }
  };

  const savePayoutSettings = async () => {
    try {
      setSavingPayout(true);
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${API}/referral/payout-settings`,
        payoutForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Payout settings saved!', {
          description: 'Your payment information has been updated.'
        });
        setPayoutSettings(payoutForm);
      }
    } catch (error) {
      console.error('Error saving payout settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save payout settings');
    } finally {
      setSavingPayout(false);
    }
  };

  const generateReferralCode = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${API}/referral/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Referral code generated!', {
          description: 'Start sharing and earning commissions today!'
        });
        fetchAllData();
      }
    } catch (error) {
      console.error('Error generating referral code:', error);
      toast.error('Failed to generate referral code');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOnSocial = (platform) => {
    const link = dashboardData?.referral_link;
    const message = encodeURIComponent(`Join Estate Flow and get automated rent collection! Use my referral link: ${link}`);
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${message}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`,
      whatsapp: `https://wa.me/?text=${message}`,
      email: `mailto:?subject=Check out Estate Flow&body=${message}`
    };
    
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const requestPayout = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${API}/referral/request-payout`,
        {
          method: 'stripe',
          details: { email: localStorage.getItem('user_email') }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Payout requested!', {
          description: `$${response.data.amount.toFixed(2)} will be processed in 3-5 business days`
        });
        setShowPayoutDialog(false);
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Error requesting payout:', error);
      toast.error(error.response?.data?.detail || 'Failed to request payout');
    }
  };

  const toggleEmailNotifications = () => {
    setEmailNotifications(!emailNotifications);
    toast.success(`Email notifications ${!emailNotifications ? 'enabled' : 'disabled'}`);
  };

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!dashboardData?.has_referral_code) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Gift className="h-6 w-6 mr-2 text-blue-600" />
            Earn Money by Referring Friends!
          </CardTitle>
          <CardDescription className="mt-2">
            Join our referral program and earn 25% recurring commission on every person you refer to Estate Flow
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-lg mb-3 text-blue-900">How It Works:</h3>
            <ul className="space-y-2 text-blue-800">
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                <span>Get your unique referral link</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                <span>Share with friends, family, or your network</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                <span>Earn 25% commission every month they stay subscribed</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                <span>Request payout once you reach $50</span>
              </li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-green-900 mb-2">Earning Example:</h4>
            <p className="text-sm text-green-800">
              If you refer 10 landlords paying $99/month each:
              <br />
              <strong className="text-lg">$247.50/month recurring income!</strong>
              <br />
              <span className="text-xs">($99 × 10 × 25% = $247.50 per month, for as long as they stay)</span>
            </p>
          </div>

          <Button 
            onClick={generateReferralCode}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700"
            size="lg"
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <><Gift className="h-4 w-4 mr-2" /> Get My Referral Link</>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { stats, referral_link, referral_code, recent_signups, recent_commissions } = dashboardData;
  const maxEarnings = Math.max(...earningsChart.map(d => d.earnings), 100);

  return (
    <div className="space-y-6">
      {/* Header with Email Toggle */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center">
            <TrendingUp className="h-6 w-6 mr-2 text-blue-600" />
            Referral Dashboard
          </h2>
          <p className="text-gray-600 mt-1">Share your link and earn 25% recurring commissions</p>
        </div>
        <Button 
          variant={emailNotifications ? "default" : "outline"}
          onClick={toggleEmailNotifications}
          className="flex items-center"
        >
          {emailNotifications ? (
            <><Bell className="h-4 w-4 mr-2" /> Notifications On</>
          ) : (
            <><BellOff className="h-4 w-4 mr-2" /> Notifications Off</>
          )}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Signups</p>
                <p className="text-3xl font-bold">{stats?.total_signups || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Referrals</p>
                <p className="text-3xl font-bold">{stats?.active_referrals || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Payout</p>
                <p className="text-3xl font-bold text-green-600">
                  ${stats?.pending_payout?.toFixed(2) || '0.00'}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Lifetime Earnings</p>
                <p className="text-3xl font-bold text-blue-600">
                  ${stats?.lifetime_earnings?.toFixed(2) || '0.00'}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different views */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="payout-settings">💳 Payout Settings</TabsTrigger>
          <TabsTrigger value="payout-history">📜 Payout History</TabsTrigger>
          <TabsTrigger value="chart">📊 Earnings Chart</TabsTrigger>
          <TabsTrigger value="leaderboard">🏆 Leaderboard</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Referral Link Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <LinkIcon className="h-5 w-5 mr-2" />
                Your Referral Link
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Input 
                  value={referral_link} 
                  readOnly 
                  className="font-mono text-sm"
                />
                <Button 
                  onClick={() => copyToClipboard(referral_link)}
                  variant="outline"
                >
                  {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Referral Code:</span>
                <Badge className="font-mono text-lg">{referral_code}</Badge>
                <Button 
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(referral_code)}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-2">Share on:</p>
                <div className="flex space-x-2">
                  <Button size="sm" variant="outline" onClick={() => shareOnSocial('twitter')}>
                    Twitter
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => shareOnSocial('facebook')}>
                    Facebook
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => shareOnSocial('linkedin')}>
                    LinkedIn
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => shareOnSocial('whatsapp')}>
                    WhatsApp
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => shareOnSocial('email')}>
                    Email
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payout Section - Always visible */}
          <Card className={`border-2 ${(stats?.pending_payout || 0) >= 50 ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Wallet className="h-8 w-8 text-green-600" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Available for Payout</h3>
                    <p className="text-2xl font-bold text-green-600">
                      ${(stats?.pending_payout || 0).toFixed(2)}
                    </p>
                    {(stats?.pending_payout || 0) < 50 && (
                      <p className="text-sm text-gray-500">
                        Minimum payout: $50.00 (${(50 - (stats?.pending_payout || 0)).toFixed(2)} more needed)
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  {!payoutSettings?.account_number && !payoutSettings?.paypal_email && !payoutSettings?.zelle_email ? (
                    <div>
                      <p className="text-sm text-amber-600 mb-2">⚠️ Set up payout method first</p>
                      <Button 
                        variant="outline"
                        onClick={() => document.querySelector('[data-value="payout-settings"]')?.click()}
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Setup Payout
                      </Button>
                    </div>
                  ) : (stats?.pending_payout || 0) >= 50 ? (
                    <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
                      <DialogTrigger asChild>
                        <Button className="bg-green-600 hover:bg-green-700">
                          <DollarSign className="h-4 w-4 mr-2" />
                          Request Payout
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Request Payout</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                            <p className="text-lg font-bold text-green-800">
                              Amount: ${(stats?.pending_payout || 0).toFixed(2)}
                            </p>
                          </div>
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <p className="text-sm text-blue-800">
                              <strong>Payout Method:</strong> {payoutForm.payout_method === 'bank_transfer' ? 'Bank Transfer (ACH)' : payoutForm.payout_method === 'visa_direct' ? 'Visa Direct (Real-time)' : payoutForm.payout_method === 'paypal' ? 'PayPal' : 'Zelle'}
                            </p>
                            {payoutForm.payout_method === 'bank_transfer' && (
                              <p className="text-sm text-blue-800 mt-1">
                                <strong>Account:</strong> {payoutForm.bank_name} - ****{payoutForm.account_number?.slice(-4)}
                              </p>
                            )}
                            {payoutForm.payout_method === 'visa_direct' && (
                              <>
                                <p className="text-sm text-blue-800 mt-1">
                                  <strong>Card:</strong> Visa ****{payoutForm.visa_card_number?.replace(/\s/g, '').slice(-4)}
                                </p>
                                <p className="text-sm text-green-700 mt-1 font-medium">
                                  ⚡ Real-time payout - Funds arrive within minutes!
                                </p>
                              </>
                            )}
                            {payoutForm.payout_method === 'paypal' && (
                              <p className="text-sm text-blue-800 mt-1">
                                <strong>PayPal:</strong> {payoutForm.paypal_email}
                              </p>
                            )}
                            {payoutForm.payout_method === 'zelle' && (
                              <p className="text-sm text-blue-800 mt-1">
                                <strong>Zelle:</strong> {payoutForm.zelle_email}
                              </p>
                            )}
                            <p className="text-sm text-blue-800 mt-2">
                              <strong>Processing Time:</strong> {payoutForm.payout_method === 'visa_direct' ? 'Instant (within minutes)' : '3-5 business days'}
                            </p>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setShowPayoutDialog(false)}>
                            Cancel
                          </Button>
                          <Button onClick={requestPayout} className="bg-green-600 hover:bg-green-700">
                            Confirm Payout Request
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  ) : (
                    <Button disabled variant="outline">
                      <DollarSign className="h-4 w-4 mr-2" />
                      Request Payout
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Signups</CardTitle>
              </CardHeader>
              <CardContent>
                {recent_signups && recent_signups.length > 0 ? (
                  <div className="space-y-3">
                    {recent_signups.map((signup, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{signup.referred_user_email}</p>
                          <p className="text-xs text-gray-600">
                            {new Date(signup.signed_up_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge className={signup.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}>
                          {signup.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No signups yet. Start sharing your link!</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Commissions</CardTitle>
              </CardHeader>
              <CardContent>
                {recent_commissions && recent_commissions.length > 0 ? (
                  <div className="space-y-3">
                    {recent_commissions.map((commission, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium text-green-600">+${commission.amount.toFixed(2)}</p>
                          <p className="text-xs text-gray-600">
                            {new Date(commission.earned_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge variant="outline">25% of ${commission.subscription_amount}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No commissions yet. Signups will generate commissions monthly!</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Payout Settings Tab */}
        <TabsContent value="payout-settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CreditCard className="h-5 w-5 mr-2" />
                Payout Settings
              </CardTitle>
              <CardDescription>
                Set up how you want to receive your referral earnings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Payout Method Selection */}
              <div className="space-y-2">
                <Label>Payout Method</Label>
                <Select
                  value={payoutForm.payout_method}
                  onValueChange={(value) => setPayoutForm(prev => ({ ...prev, payout_method: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select payout method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">🏦 Bank Transfer (ACH)</SelectItem>
                    <SelectItem value="visa_direct">💳 Visa Direct (Real-time to Card)</SelectItem>
                    <SelectItem value="paypal">💰 PayPal</SelectItem>
                    <SelectItem value="zelle">⚡ Zelle</SelectItem>
                  </SelectContent>
                </Select>
                {payoutForm.payout_method === 'visa_direct' && (
                  <p className="text-xs text-blue-600 mt-1">
                    ✨ Instant payout to your Visa debit card - Powered by Visa
                  </p>
                )}
              </div>

              {/* Bank Transfer Fields */}
              {payoutForm.payout_method === 'bank_transfer' && (
                <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                  <h4 className="font-medium flex items-center">
                    <Building2 className="h-4 w-4 mr-2" />
                    Bank Account Details
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Bank Name</Label>
                      <Input
                        placeholder="e.g., Chase, Bank of America"
                        value={payoutForm.bank_name}
                        onChange={(e) => setPayoutForm(prev => ({ ...prev, bank_name: e.target.value }))}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Bank Branch</Label>
                      <Input
                        placeholder="e.g., Main Street Branch, Downtown"
                        value={payoutForm.bank_branch}
                        onChange={(e) => setPayoutForm(prev => ({ ...prev, bank_branch: e.target.value }))}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Account Holder Name</Label>
                      <Input
                        placeholder="Name on the account"
                        value={payoutForm.account_holder_name}
                        onChange={(e) => setPayoutForm(prev => ({ ...prev, account_holder_name: e.target.value }))}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Account Type</Label>
                      <Select
                        value={payoutForm.account_type}
                        onValueChange={(value) => setPayoutForm(prev => ({ ...prev, account_type: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="checking">Checking</SelectItem>
                          <SelectItem value="savings">Savings</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Account Number</Label>
                      <Input
                        placeholder="Enter account number"
                        value={payoutForm.account_number}
                        onChange={(e) => setPayoutForm(prev => ({ ...prev, account_number: e.target.value }))}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Routing Number</Label>
                      <Input
                        placeholder="9-digit routing number"
                        value={payoutForm.routing_number}
                        onChange={(e) => setPayoutForm(prev => ({ ...prev, routing_number: e.target.value }))}
                        maxLength={9}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Visa Direct Fields */}
              {payoutForm.payout_method === 'visa_direct' && (
                <div className="space-y-4 p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center">
                      <CreditCard className="h-4 w-4 mr-2 text-blue-600" />
                      Visa Direct - Real-time Payout
                    </h4>
                    <Badge className="bg-blue-600">Instant</Badge>
                  </div>
                  
                  <div className="bg-white/70 p-3 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <strong>How it works:</strong> Funds are pushed directly to your Visa debit card in real-time using Visa Direct (OCT). 
                      Perfect for Caribbean banks - works with any Visa debit card worldwide.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label>Cardholder Name (as shown on card)</Label>
                      <Input
                        placeholder="JOHN A DOE"
                        value={payoutForm.visa_card_holder_name}
                        onChange={(e) => setPayoutForm(prev => ({ ...prev, visa_card_holder_name: e.target.value.toUpperCase() }))}
                        className="uppercase"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Visa Debit Card Number</Label>
                      <Input
                        placeholder="4111 1111 1111 1111"
                        value={payoutForm.visa_card_number}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 16);
                          const formatted = value.replace(/(\d{4})(?=\d)/g, '$1 ');
                          setPayoutForm(prev => ({ ...prev, visa_card_number: formatted }));
                        }}
                        maxLength={19}
                      />
                      <p className="text-xs text-gray-500">Must be a Visa debit card (starts with 4)</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Card Expiry (MM/YY)</Label>
                      <Input
                        placeholder="12/28"
                        value={payoutForm.visa_card_expiry}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, '').slice(0, 4);
                          if (value.length >= 2) {
                            value = value.slice(0, 2) + '/' + value.slice(2);
                          }
                          setPayoutForm(prev => ({ ...prev, visa_card_expiry: value }));
                        }}
                        maxLength={5}
                      />
                    </div>
                  </div>
                  
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 mt-4">
                    <p className="text-xs text-amber-800">
                      <strong>🔒 Security:</strong> Your card details are encrypted and securely stored. 
                      Powered by Visa Direct for fast, secure payouts. Funds typically arrive within minutes.
                    </p>
                  </div>
                </div>
              )}

              {/* PayPal Fields */}
              {payoutForm.payout_method === 'paypal' && (
                <div className="space-y-4 p-4 border rounded-lg bg-blue-50">
                  <h4 className="font-medium">PayPal Details</h4>
                  <div className="space-y-2">
                    <Label>PayPal Email Address</Label>
                    <Input
                      type="email"
                      placeholder="your-email@example.com"
                      value={payoutForm.paypal_email}
                      onChange={(e) => setPayoutForm(prev => ({ ...prev, paypal_email: e.target.value }))}
                    />
                    <p className="text-xs text-gray-500">
                      Make sure this email is registered with PayPal
                    </p>
                  </div>
                </div>
              )}

              {/* Zelle Fields */}
              {payoutForm.payout_method === 'zelle' && (
                <div className="space-y-4 p-4 border rounded-lg bg-purple-50">
                  <h4 className="font-medium">Zelle Details</h4>
                  <div className="space-y-2">
                    <Label>Zelle Email or Phone</Label>
                    <Input
                      placeholder="Email or phone registered with Zelle"
                      value={payoutForm.zelle_email}
                      onChange={(e) => setPayoutForm(prev => ({ ...prev, zelle_email: e.target.value }))}
                    />
                    <p className="text-xs text-gray-500">
                      Enter the email or phone number linked to your Zelle account
                    </p>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button 
                  onClick={savePayoutSettings}
                  disabled={savingPayout}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {savingPayout ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><CheckCircle className="h-4 w-4 mr-2" /> Save Payout Settings</>
                  )}
                </Button>
              </div>

              {/* Current Settings Display */}
              {payoutSettings && (payoutSettings.account_number || payoutSettings.paypal_email || payoutSettings.zelle_email) && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-medium text-green-800 flex items-center mb-2">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Current Payout Method
                  </h4>
                  {payoutSettings.payout_method === 'bank_transfer' && (
                    <p className="text-sm text-green-700">
                      Bank Transfer: {payoutSettings.bank_name} - ****{payoutSettings.account_number?.slice(-4)} ({payoutSettings.account_type})
                    </p>
                  )}
                  {payoutSettings.payout_method === 'paypal' && (
                    <p className="text-sm text-green-700">
                      PayPal: {payoutSettings.paypal_email}
                    </p>
                  )}
                  {payoutSettings.payout_method === 'zelle' && (
                    <p className="text-sm text-green-700">
                      Zelle: {payoutSettings.zelle_email}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payout History Tab */}
        <TabsContent value="payout-history">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <History className="h-5 w-5 mr-2" />
                Payout History
              </CardTitle>
              <CardDescription>
                Track all your payout requests and their status
              </CardDescription>
            </CardHeader>
            <CardContent>
              {payoutHistory.length > 0 ? (
                <div className="space-y-3">
                  {payoutHistory.map((payout, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`p-2 rounded-full ${
                          payout.status === 'completed' ? 'bg-green-100' :
                          payout.status === 'pending' ? 'bg-yellow-100' :
                          payout.status === 'processing' ? 'bg-blue-100' :
                          'bg-red-100'
                        }`}>
                          <DollarSign className={`h-5 w-5 ${
                            payout.status === 'completed' ? 'text-green-600' :
                            payout.status === 'pending' ? 'text-yellow-600' :
                            payout.status === 'processing' ? 'text-blue-600' :
                            'text-red-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-semibold">${payout.amount?.toFixed(2)}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(payout.requested_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={
                          payout.status === 'completed' ? 'bg-green-100 text-green-800' :
                          payout.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          payout.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {payout.status?.charAt(0).toUpperCase() + payout.status?.slice(1)}
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1">
                          via {payout.method === 'bank_transfer' ? 'Bank Transfer' : 
                               payout.method === 'paypal' ? 'PayPal' : 
                               payout.method === 'zelle' ? 'Zelle' : payout.method}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <History className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No payout history yet</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Your payout requests will appear here
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="chart">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Earnings Over Time
              </CardTitle>
              <CardDescription>Your monthly commission earnings for the last 12 months</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {earningsChart.length > 0 ? (
                  earningsChart.map((data, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <span className="text-sm text-gray-600 w-20">{data.month}</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-8 relative">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-emerald-500 h-8 rounded-full flex items-center justify-end pr-3"
                          style={{ width: `${(data.earnings / maxEarnings) * 100}%` }}
                        >
                          {data.earnings > 0 && (
                            <span className="text-xs font-semibold text-white">
                              ${data.earnings.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-8">No earnings data yet. Start referring to see your chart!</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leaderboard">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Trophy className="h-5 w-5 mr-2 text-yellow-500" />
                Top Referrers Leaderboard
              </CardTitle>
              <CardDescription>See how you rank against other referrers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {leaderboard.length > 0 ? (
                  leaderboard.map((entry, index) => (
                    <div 
                      key={index} 
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        entry.is_you ? 'bg-blue-50 border-blue-300' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100">
                          {entry.rank === 1 && <Crown className="h-6 w-6 text-yellow-500" />}
                          {entry.rank === 2 && <Medal className="h-6 w-6 text-gray-400" />}
                          {entry.rank === 3 && <Medal className="h-6 w-6 text-orange-400" />}
                          {entry.rank > 3 && <span className="font-bold text-gray-600">#{entry.rank}</span>}
                        </div>
                        <div>
                          <p className="font-semibold">
                            {entry.name}
                            {entry.is_you && <Badge className="ml-2 bg-blue-600">You</Badge>}
                          </p>
                          <p className="text-sm text-gray-600">{entry.total_signups} signups</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">${entry.lifetime_earnings.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">earned</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-8">No leaderboard data yet. Be the first!</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReferralDashboard;
